import { useLazyQuery, useMutation } from '@apollo/client';
import { useAuth } from 'components/custom/authprovider';
import { CREATE_ADMIN_USER } from 'graphql/admin/mutations/users';
import { UPDATE_ADMIN_USER } from 'graphql/admin/mutations/users';
import { GET_ADMIN_USER } from 'graphql/admin/queries/users';
import { GET_ADMIN_USERS } from 'graphql/admin/queries/users';
import React, { useEffect, useRef, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import Select from 'react-select';
import { Button, Card, CardBody, CardHeader, Col, Container, Row } from 'reactstrap';
import { SAText } from 'utils/functions';
import { SimpleFormatPhone } from 'utils/functions';
import sendMutationContext from 'utils/sendContext';
import AdminNav from './AdminNav';
import NewUser from './components/newUser';
import UserCard from './components/user';

function UserManagement() {
  const { wt } = useAuth();
  const { userPhone } = useParams();
  const [inputPhone, setInputPhone] = useState("");
  const history = useHistory();
  const [logoutFlag, setLogoutFlag] = useState();

  const location = useLocation();
  const initialPathRef = useRef(location.pathname);
  const basePath = (() => {
    const current = initialPathRef.current;
    const lastSlashIndex = current.lastIndexOf('/');

    if (current.slice(lastSlashIndex, current.length) == "/user-management") {
      return current + "/";
    }

    return current.slice(0, lastSlashIndex + 1);
  })();

  const [isNewUser, setIsNewUser] = useState(false);
  const [phone, setPhone] = useState("");
  const [resetForm, setResetForm] = useState(false);

  const [user, setUser] = useState(null);
  const [getAdminUser] = useLazyQuery(GET_ADMIN_USER, sendMutationContext(wt, setLogoutFlag));

  const [userPhoneData, setUserPhoneData] = useState([]);
  const [getAdminUsers] = useLazyQuery(GET_ADMIN_USERS, sendMutationContext(wt, setLogoutFlag));
  const [updateAdminUser] = useMutation(UPDATE_ADMIN_USER, sendMutationContext(wt, setLogoutFlag));
  const [createAdminUser] = useMutation(CREATE_ADMIN_USER, sendMutationContext(wt, setLogoutFlag));

  const customSelectStyle = {
    control: (provided, state) => ({
      ...provided,
      backgroundColor: 'white',
      borderColor: state.isFocused ? 'blue' : 'gray',
      boxShadow: state.isFocused ? '0 0 5px rgba(0, 0, 255, 0.5)' : 'none',
      '&:hover': {
        borderColor: 'blue',
      },
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected
        ? 'lightblue'
        : state.isFocused
          ? 'lightblue'
          : 'white',
      color: state.isSelected ? 'white' : 'black',
      '&:hover': {
        backgroundColor: 'lightblue',
      },
    }),
  };

  const CreateAdminUser = async (newUser) => {
    createAdminUser({
      variables: {
        phone: newUser.phone,
        name: newUser.name,
        username: newUser.username,
        email: newUser.email,
        enabled: newUser.enabled,
        permissions: parseInt(newUser.permissions, 10),
      },
    })
      .then((res) => {
        const status = res?.data?.createAdminUser?.status ?? false;

        if (!status) {
          SAText("Error", res.data.createAdminUser.error, "error");
          return;
        }

        SAText("Success", "User has been created!", "success");
      })
      .then(() => {
        updateUserPhoneList();
        setResetForm(true);
      });
  };

  const UpdateAdminUser = (user, {
    phone = user.phone,
    name = user.name,
    username = user.username,
    email = user.email,
    enabled = user.enabled,
    permissions = user.permissions,
  }) => {
    updateAdminUser({
      variables: {
        userid: user.userid,
        name: name,
        phone: phone,
        username: username,
        email: email,
        enabled: enabled,
        permissions: parseInt(permissions, 10),
      },
    })
      .then((res) => {
        const status = res?.data?.updateAdminUser?.status ?? false;

        if (!status) {
          SAText("Error", res.data.updateAdminUser.error, "error");
          return;
        }

        setUser(res?.data?.updateAdminUser?.user ?? {});
      });
  };

  const updateUserPhoneList = () => {
    getAdminUsers()
      .then((res) => {
        setUserPhoneData(() => {
          const users = (res?.data?.getAdminUsers?.users ?? [])
            .slice()
            .filter((fUser) => {
            if (user && fUser.userid === user.userid) {
              return user.active;
            }

            return true;
          })
            .sort((a, b) => (a.name.split(" ")[1] || "").localeCompare((b.name.split(" ")[1] || "" )))
            .map((item) => ({
              value: item.userid,
              label: `${SimpleFormatPhone(item.phone)} - ${item.name || ""}`
            }));

          return users;
        });
      });
  };

  useEffect(() => {
    if (userPhone) {
      setPhone(userPhone.replaceAll("-", ""));
    }

    updateUserPhoneList();
  }, []);

  useEffect(() => {
    if (isNewUser) {
      history.replace({ pathname: basePath });
    }
  }, [isNewUser]);

  useEffect(() => {
    if (phone) {
      history.replace({ pathname: basePath + SimpleFormatPhone(phone) });
      return;
    }

    history.replace({ pathname: basePath });
  }, [phone]);

  useEffect(() => {
    if (phone) {
      getAdminUser({
        variables: {
          phone: phone,
        },
      })
        .then((res) => {
          const status = res?.data?.getAdminUser?.status ?? false;

          if (status) {
            setUser(res?.data?.getAdminUser?.user ?? {});
          }
        });
    }
  }, [phone]);

  useEffect(() => {
    if (logoutFlag) {
      history.push("/admin/logout");
    }
  }, [logoutFlag]);

  return (
    <div className="admin-wrapper">
      <AdminNav />

      <Container fluid className="content">
        <Row>
          <Col md="12">
            <Card className="card-dark">
              <CardHeader className="card-header-dark">
                <Row>
                  <Col sm="7">
                    User Management
                  </Col>

                  <Col sm="2" style={{ textAlign: "right" }}>
                    <Button
                      color="success"
                      onClick={() => {
                        setIsNewUser(true);
                        }}
                        >
                      New User
                    </Button>
                  </Col>

                  <Col sm="3" style={{ textAlign: "right" }}>
                    {userPhoneData &&
                      <Select
                        options={userPhoneData}
                        inputValue={inputPhone}
                        styles={customSelectStyle}
                        onInputChange={(e) => {
                          setIsNewUser(false);
                          if (e.length >= 3 && !isNaN(parseInt(e, 10))) {
                            setInputPhone(SimpleFormatPhone(e));
                            return;
                          }

                          setInputPhone(e);
                        }}
                        onChange={(e) => {
                          getAdminUser({
                            variables: {
                              userid: e.value,
                            },
                          })
                          .then((res) => {
                              const status = res?.data?.getAdminUser?.status ?? false;

                              if (status) {
                                const user = res?.data?.getAdminUser?.user ?? {};

                                if (Object.keys(user).length === 0) {
                                  return;
                                }

                                setUser(user);

                                history.replace({ pathname: basePath + SimpleFormatPhone(user.phone) });
                              }
                            });
                        }}
                        isSearchable={true}
                        placeholder="User search..."
                      />
                    }
                  </Col>
                </Row>
              </CardHeader>
              <CardBody>
                <Row>
                  {isNewUser &&
                    <Col lg="6" md="6" className="customer-card">
                      <NewUser resetForm={resetForm} setResetForm={setResetForm} createAdminUser={CreateAdminUser} />
                    </Col>
                  }
                  {!isNewUser &&
                    <>
                  {user &&
                    <Col lg="6" md="6" className="customer-card">
                      <UserCard user={user} setUser={setUser} updateAdminUser={UpdateAdminUser} />
                    </Col>
                  }
                  </>
                }
                </Row>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
}

export default UserManagement;